import { useState } from 'react';
import { Checkbox, Divider, Input, List, Message } from 'semantic-ui-react';
import { Button } from '~/components';
import { usePromiseWatcher } from '~/hooks';
import {
  finishTwoFactorSetup,
  generateBackupCodes,
  registerPasskey,
  sendTwoFactorEmailCode,
  verifyEnrollmentEmailCode,
} from '~/api/auth';
import AuthForm from '../components/AuthForm';
import redirectAfterLogin from './redirectAfterLogin';

type Step = 'choose' | 'passkey' | 'email' | 'backup';

export default function Setup2FA() {
  const [step, setStep] = useState<Step>('choose');
  const [deviceName, setDeviceName] = useState('');
  const [emailCode, setEmailCode] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  const [codes, setCodes] = useState<string[]>([]);
  const [savedConfirmed, setSavedConfirmed] = useState(false);

  // Switch between enrollment methods, clearing any half-entered state so the
  // other method starts clean.
  const goTo = (next: Step) => () => {
    setEmailCode('');
    setCodeSent(false);
    setStep(next);
  };

  // Both enrollment methods converge here: generate recovery codes, then show them.
  const showBackupCodes = async () => {
    const { codes: generated } = await generateBackupCodes();
    setCodes(generated);
    setStep('backup');
  };

  const { execute: addPasskey, status: passkeyStatus } = usePromiseWatcher(
    async () => {
      await registerPasskey(deviceName.trim() || undefined);
      await showBackupCodes();
    },
    {
      loading: 'Registering your passkey',
      success: 'Passkey registered',
      error: 'Could not register a passkey. Please try again.',
    },
    [deviceName]
  );

  const { execute: sendCode, status: sendStatus } = usePromiseWatcher(
    async () => {
      await sendTwoFactorEmailCode();
      setCodeSent(true);
    },
    { loading: 'Sending a code to your email', success: 'Code sent', error: 'Could not send a code.' },
    []
  );

  const { execute: verifyCode, status: verifyStatus } = usePromiseWatcher(
    async () => {
      await verifyEnrollmentEmailCode(emailCode.trim());
      await showBackupCodes();
    },
    {
      loading: 'Verifying your code',
      success: 'Email verified',
      error: 'That code was invalid or expired. Please try again.',
    },
    [emailCode]
  );

  const { execute: finish, status: finishStatus } = usePromiseWatcher(
    async () => redirectAfterLogin(await finishTwoFactorSetup()),
    { loading: null, success: null, error: 'Could not finish setup. Please try again.' },
    []
  );

  const switchLink = (label: string, onClick: () => void) => (
    <Message className="auth-page__footer-message">
      <Button variant="link" content={label} style={{ marginTop: 0 }} onClick={onClick} />
    </Message>
  );

  return (
    <AuthForm header="Set up two-step verification" width="480px">
      {step === 'choose' && (
        <>
          <Message
            info
            content="Your account requires two-step verification. Add a second factor below, then save your backup codes."
          />
          <Button
            fluid
            secondary
            size="large"
            content="Register a passkey"
            onClick={goTo('passkey')}
          />
          <Divider horizontal>or</Divider>
          <Button
            fluid
            outlined
            size="large"
            content="Verify with an email code"
            onClick={goTo('email')}
          />
        </>
      )}

      {step === 'passkey' && (
        <>
          <Message
            info
            size="tiny"
            content="A passkey uses Face ID, Touch ID, Windows Hello, or a security key. You can name this device so it's easy to recognize later."
          />
          <Input
            fluid
            size="large"
            icon="tag"
            iconPosition="left"
            placeholder="Device name (optional, e.g. 'MacBook')"
            value={deviceName}
            onChange={(_e, { value }) => setDeviceName(value)}
            style={{ marginTop: 'var(--small)', fontSize: '14px' }}
          />
          <Button
            fluid
            secondary
            size="large"
            content="Register a passkey"
            loading={passkeyStatus === 'pending'}
            disabled={passkeyStatus === 'pending'}
            onClick={addPasskey}
          />
          {switchLink('Verify with an email code instead', goTo('email'))}
        </>
      )}

      {step === 'email' && (
        <>
          <Message
            info
            size="tiny"
            content="We'll email a 6-digit code to confirm you can receive verification codes."
          />
          <Button
            fluid
            outlined
            size="large"
            content={codeSent ? 'Resend code' : 'Email me a code'}
            loading={sendStatus === 'pending'}
            disabled={sendStatus === 'pending'}
            onClick={sendCode}
          />
          {codeSent && (
            <>
              <Input
                fluid
                size="large"
                icon="lock"
                iconPosition="left"
                placeholder="6-digit code"
                value={emailCode}
                autoComplete="one-time-code"
                onChange={(_e, { value }) => setEmailCode(value)}
                style={{ marginTop: 'var(--small)', fontSize: '14px' }}
              />
              <Button
                fluid
                secondary
                size="large"
                content="Verify code"
                style={{ marginTop: 'var(--small)' }}
                loading={verifyStatus === 'pending'}
                disabled={verifyStatus === 'pending' || emailCode.trim().length === 0}
                onClick={verifyCode}
              />
            </>
          )}
          {switchLink('Register a passkey instead', goTo('passkey'))}
        </>
      )}

      {step === 'backup' && (
        <>
          <Message
            warning
            header="Save your backup codes"
            content="Store these somewhere safe. Each code can be used once if you lose access to your passkey and email. They will not be shown again."
          />
          <List divided relaxed className="card" style={{ padding: 'var(--small)' }}>
            {codes.map((code) => (
              <List.Item key={code}>
                <code style={{ fontSize: '16px', letterSpacing: '2px' }}>{code}</code>
              </List.Item>
            ))}
          </List>
          <Checkbox
            label="I have saved my backup codes"
            checked={savedConfirmed}
            onChange={(_e, { checked }) => setSavedConfirmed(!!checked)}
            style={{ marginTop: 'var(--small)' }}
          />
          <Button
            fluid
            secondary
            size="large"
            style={{ marginTop: 'var(--small)' }}
            content="Finish and continue"
            disabled={!savedConfirmed || finishStatus === 'pending' || finishStatus === 'success'}
            loading={finishStatus === 'pending' || finishStatus === 'success'}
            onClick={finish}
          />
        </>
      )}
    </AuthForm>
  );
}
